/**
 * Referee-permission attestation copy (WP-F5) — pure, transport-free, unit-tested.
 *
 * The new-request wizard asks the candidate to confirm that the person whose address they are
 * handing over would be OK to hear from us. Three strings carry that: the statement the checkbox
 * labels, the hint when the tick is missing, and the message when the server refuses the send.
 *
 * They live here rather than in the route because the referee's name takes a DIFFERENT grammatical
 * role in each, and getting that wrong is invisible until a name goes missing:
 *
 *  - the statement OPENS on the referee, so its no-name fallback is a capitalised subject, "They";
 *  - the hint puts the referee after a verb, so its fallback is the object "them".
 *
 * One shared fallback for both would ship "them would be OK with receiving an email…". Keeping the
 * two forms in one small module puts that distinction where a reader trips over it.
 */
import { firstNameOf } from './timeline';

/**
 * The referee's first name in OBJECT position — "we email …", "a way to reach …". `firstNameOf`
 * degrades to a lowercase "they" when there is no name (step 1 of the wizard gates on one, so this
 * is a fallback, not a path), which reads wrong after a verb.
 */
export function refereeObject(refereeName: string | null | undefined): string {
  const first = firstNameOf(refereeName);
  return first === 'they' ? 'them' : first;
}

/** The referee's first name in SUBJECT position — sentence-initial, so the fallback capitalises. */
export function refereeSubject(refereeName: string | null | undefined): string {
  const first = firstNameOf(refereeName);
  return first === 'they' ? 'They' : first;
}

/** The statement the candidate is asserting, rendered as the attestation checkbox's label. */
export function permissionStatement(refereeName: string | null | undefined): string {
  return `${refereeSubject(refereeName)} would be OK with receiving an email from me about being my referee.`;
}

/** The tick is missing — say what to do, on the control itself. */
export function unconfirmedMessage(refereeName: string | null | undefined): string {
  return `Please confirm before we email ${refereeObject(refereeName)}.`;
}

/**
 * The server refused the send for want of the attestation (`referee_permission_required`). It names
 * nobody, so it is a constant rather than a builder — there is no name to thread through.
 */
export const SERVER_REFUSED_MESSAGE = 'We couldn’t send that without your confirmation.';
